import type { EquipmentItem, InventoryFilters } from './types'

/**
 * The inventory filters, applied on the device (issue #70).
 *
 * `GET /api/items` takes `q`, `category`, `locationId` and `fixtureModelId` and
 * still does, but the web client stops asking for a *filtered* list: it holds
 * the whole live inventory under one cache entry and narrows it here. Two
 * reasons, and the second is the one the issue is about:
 *
 *   - **One copy.** A cache entry per filter combination is the same records
 *     written into IndexedDB several times over, each able to go stale
 *     separately from the others.
 *   - **A filter set nobody visited is a filter set nobody has offline.** The
 *     persisted cache is a dehydration of what was actually fetched, so walking
 *     into a cellar and filtering by that location used to give a paused query
 *     and a spinner — over rows that were already on the device under a
 *     different key.
 *
 * It mirrors `EquipmentItemRepository::applyFilters()` deliberately and must go
 * on doing so: the same filters have to mean the same thing whether they were
 * answered by MySQL or by this function, or a walker sees a list change under
 * them when the signal comes back.
 *
 * It lives in `@mads/shared` beside the queue's types, for the reason everything
 * else here does — React Native adopts it unchanged.
 */
export function matchesInventoryFilters(item: EquipmentItem, filters: InventoryFilters): boolean {
  return (
    matchesQuery(item, filters.q) &&
    matchesCategory(item, filters.category) &&
    matchesLocation(item, filters.locationId) &&
    matchesFixtureModel(item, filters.fixtureModelId)
  )
}

/** Every live item the filters admit, in the order the API sent them. */
export function filterInventory(
  items: readonly EquipmentItem[],
  filters: InventoryFilters,
): EquipmentItem[] {
  return items.filter((item) => matchesInventoryFilters(item, filters))
}

/**
 * `i.name LIKE :q OR i.brand LIKE :q OR i.notes LIKE :q OR i.madsCode LIKE :q OR
 * f.name LIKE :q`, with `%…%` either side.
 *
 * The fixture model's name counts as the item's (issue #30) — searching for
 * "Fresnel" finds eight lanterns that keep their spec on a shared model.
 * `displayName` is deliberately *not* searched: it is `brand` and `name` and
 * `madsCode` already, so adding it would only let a query straddle the
 * separators and match something MySQL would not.
 *
 * Case-insensitive because the columns are, on a `utf8mb4_…_ci` collation.
 */
function matchesQuery(item: EquipmentItem, query: string | undefined): boolean {
  const needle = (query ?? '').trim().toLowerCase()

  if (needle === '') return true

  return [item.name, item.brand, item.notes, item.madsCode, item.fixtureModel?.name].some(
    (field) => field != null && field.toLowerCase().includes(needle),
  )
}

function matchesCategory(item: EquipmentItem, category: InventoryFilters['category']): boolean {
  return category === undefined || category === 'All' || item.category === category
}

/**
 * A binned location matches nothing (issue #9). Nothing extra is needed for
 * that here: `InventoryPresenter::locationRef()` already answers null for kit
 * sitting on a location in the trash, so such an item reads as unassigned and
 * simply fails this test — which is what the `l.deletedAt IS NULL` in the query
 * builder does server-side.
 */
function matchesLocation(item: EquipmentItem, locationId: InventoryFilters['locationId']): boolean {
  return locationId === undefined || locationId === 'All' || item.location?.id === locationId
}

function matchesFixtureModel(
  item: EquipmentItem,
  fixtureModelId: InventoryFilters['fixtureModelId'],
): boolean {
  return (
    fixtureModelId === undefined ||
    fixtureModelId === 'All' ||
    item.fixtureModel?.id === fixtureModelId
  )
}
